import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { URL_MAP } from '../../shared/urlMap';
import { environment as ENV } from '../../../environments/environment';
import {
  ProgramaLineaNegocioDTO,
  ProgramaLineaNegocioRequest,
} from '../../models/programas-linea-negocio/programa-linea-negocio.model';

@Injectable({
  providedIn: 'root',
})
export class ProgramasLineaNegocioService {
  private apiUrl = `${ENV.HOST}${URL_MAP.API.PROGRAMAS_LINEA_NEGOCIO}`;

  constructor(private http: HttpClient) {}

  getProgramasPorLineaNegocio(
    params: ProgramaLineaNegocioRequest,
  ): Observable<ProgramaLineaNegocioDTO[]> {
    const prm_linea_neg = String(params.prm_linea_neg ?? '').trim();
    if (!prm_linea_neg) {
      return of([]);
    }

    const body = {
      nombreClave: 'getProgGrupoProgXLnNeg',
      jsonParametros: JSON.stringify({ prm_linea_neg }),
    };

    return this.http.post<unknown>(this.apiUrl, body).pipe(
      map((response) =>
        ProgramasLineaNegocioService.extractItemsArray(response).map((item) =>
          ProgramasLineaNegocioService.mapItemToDto(item),
        ),
      ),
    );
  }

  private static extractItemsArray(raw: unknown): any[] {
    const visitados = new WeakSet<object>();

    const extraer = (r: unknown, depth: number): any[] => {
      if (r == null || depth > 10) {
        return [];
      }
      if (Array.isArray(r)) {
        return r;
      }
      if (typeof r === 'string') {
        const t = r.trim();
        if (!t) {
          return [];
        }
        if (t.startsWith('{') || t.startsWith('[')) {
          try {
            return extraer(JSON.parse(t), depth + 1);
          } catch {
            return [];
          }
        }
        return [];
      }
      if (typeof r !== 'object') {
        return [];
      }
      const o = r as Record<string, unknown>;
      if (visitados.has(o)) {
        return [];
      }
      visitados.add(o);

      const shells = [
        'data',
        'Data',
        'resultado',
        'Resultado',
        'response',
        'Response',
        'result',
        'Result',
        'jsonRespuesta',
        'JsonRespuesta',
        'body',
        'Body',
        'content',
        'Content',
      ];
      for (const key of shells) {
        const inner = o[key];
        if (inner != null) {
          const got = extraer(inner, depth + 1);
          if (got.length > 0) {
            return got;
          }
        }
      }

      const listKeys = [
        'programas',
        'Programas',
        'lista',
        'Lista',
        'items',
        'Items',
        'registros',
        'Registros',
      ];
      for (const key of listKeys) {
        const v = o[key];
        if (Array.isArray(v)) {
          return v;
        }
      }

      return [];
    };

    return extraer(raw, 0);
  }

  private static mapItemToDto(item: any): ProgramaLineaNegocioDTO {
    const programa = String(
      item?.programa ??
        item?.Programa ??
        item?.nombre ??
        item?.Nombre ??
        item?.prm_programa ??
        item?.codigoPrograma ??
        '',
    ).trim();
    const grupo_programa = String(
      item?.grupo_programa ??
        item?.grupoPrograma ??
        item?.GrupoPrograma ??
        item?.grp_programa ??
        item?.prm_grp_programa ??
        '',
    ).trim();
    const tipoPrimaRaw =
      item?.tipoPrima ?? item?.TipoPrima ?? item?.tipo_prima ?? '';
    const tipoPrima = tipoPrimaRaw
      ? String(tipoPrimaRaw).trim()
      : undefined;
    return {
      programa,
      grupo_programa,
      ...(tipoPrima ? { tipoPrima } : {}),
    };
  }
}
